import type {EventPreviewDateTime, PreviewShortcut, PreviewShortcutClickedDetail, WizardCheckoutPreviewSyncArgs} from '@Components/event-wizard/preview/preview.types';
import {EVENT_PAGE_CUSTOM_BG_CLASS, EVENT_PAGE_MAIN_CONTAINER_ID} from '@Components/event-wizard/preview/preview.types';
import type {TicketingConfig, TicketingFeeParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {getEffectiveQuestionSectionTitle, getEffectiveRegisterBtnText} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import {computeTicketPriceBreakdown} from '@Components/event-wizard/event-wizard-ticketing/ticketing-fees.helper';
import {EVENT_PAGE_ACTION_FILL_CSS_VAR, EVENT_PAGE_ACTION_FILL_HOVER_CSS_VAR, EVENT_PAGE_ACTION_LABEL_CSS_VAR, applyEventPageTheme, applyEventPageThemeToFooter, getEventPageThemeFromPalette} from '@Libraries/event-page-theme.library';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {
  COUNTDOWN_ID,
  COVER_PHOTO_BACKGROUND_ID,
  COVER_PHOTO_CONTAINER_ID,
  OUTSIDE_CLICK_EVENT_TYPES,
  PREVIEW_SHORTCUT_CLASS,
  PREVIEW_SHORTCUT_CLICKED_EVENT,
  COVER_PHOTO_ID,
  COVER_PHOTO_PLACEHOLDER_ICON_ID,
  CUSTOM_EVENT_COUNTDOWN_WIZARD_UPDATED,
  CUSTOM_EVENT_WIZARD_CHECKOUT_SYNC,
  DATE_TIME_EMPTY_ID,
  DATE_TIME_END_SUBTITLE_ID,
  DATE_TIME_END_TITLE_ID,
  DATE_TIME_ICON_START_DATE_ID,
  DATE_TIME_ICON_START_MONTH_ID,
  DATE_TIME_MOBILE_END_TITLE_ID,
  DATE_TIME_MOBILE_START_TITLE_ID,
  DATE_TIME_MULTIDAY_ID,
  DATE_TIME_RECURRING_DAILY_ID,
  DATE_TIME_RECURRING_MONTHLY_ID,
  DATE_TIME_RECURRING_WEEKLY_MULTI_ID,
  DATE_TIME_RECURRING_WEEKLY_SINGLE_ID,
  DATE_TIME_SINGLE_DAY_ID,
  DATE_TIME_START_SUBTITLE_ID,
  DATE_TIME_START_TITLE_ID,
  DATE_TIME_TIMEZONE_ID,
  DESCRIPTION_ID,
  INLINE_HOVER_ATTRIBUTES,
  ORGANISER_EMAIL,
  ORGANISER_NAME,
  ORGANISER_PHONE,
  ORGANISER_PROFILE_PICTURE,
  ORGANISER_SOCIAL_CONTAINER,
  ORGANISER_SOCIAL_FACEBOOK,
  ORGANISER_SOCIAL_INSTAGRAM,
  ORGANISER_SOCIAL_LINKEDIN,
  ORGANISER_SOCIAL_PINTEREST,
  ORGANISER_SOCIAL_TIKTOK,
  ORGANISER_SOCIAL_X,
  ORGANISER_SOCIAL_YOUTUBE,
  PREVIEW_IFRAME_ID,
  REGISTRATION_CTA_ID,
  STYLE_COLOR_ID,
  STYLE_DARK_TEXT_COLOR_ID,
  STYLE_LIGHT_BG_COLOR_ID,
  STYLE_LIGHTER_BG_COLOR_ID,
  TITLE_ID,
  VENUE_ICON_EMPTY_ID,
  VENUE_ICON_GOOGLE_MEET_ID,
  VENUE_ICON_MAP_ID,
  VENUE_ICON_ONLINE_ID,
  VENUE_ICON_ZOOM_ID,
  VENUE_MAP_ADDRESS_DIRECTIONS_ID,
  VENUE_MAP_ADDRESS_ID,
  VENUE_MAP_ID,
  VENUE_MAP_LOCATION_ID,
  VENUE_MAP_NAME_ID,
  VENUE_ONLINE_GOOGLE_MEET_ID,
  VENUE_ONLINE_ID,
  VENUE_ONLINE_NON_URL_ID,
  VENUE_ONLINE_NON_URL_TEXT_ID,
  VENUE_ICON_ONLINE_NON_URL_ID,
  VENUE_ONLINE_ZOOM_ID,
  VENUE_SUMMARY_SUBTITLE,
  VENUE_SUMMARY_TITLE,
  WATERMARK_ID,
} from '@Components/event-wizard/preview/preview.types';
import {RegistrationMode, EventWizardProgressStep} from '@Components/event-wizard/event-wizard.types';
import {
  areDatesEqual,
  getMonthDateFormat,
  getReadableDayMonthDateFormatForDate,
  getReadableShortMonthForIndex,
  getReadableTimeForDate,
  getTimeDifferenceFromNowInMilliseconds,
  getTimeZoneOffsetFromUTCOffsetInMinutes,
} from '@Utils/date.util';
import type {EventWizardVenue} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import type {TimeZone} from 'timezones-list';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import type {OrganiserDetails} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import type {EventColorScheme} from '@Components/event-wizard/event-style/event-style.types';
import {CUSTOM_EVENT_GUEST_INFO_PHONE_TOGGLED, getExtraShortWeekdaysString, loadFontFamilyInIframe} from '@Libraries/events.library';
import type {EventRegistrationFields} from '@Components/events/events.types';
import {isGoogleMeetLink, isLikelyUrl, isZoomLink} from '@Utils/url.util';
import {getOrganiserProfilePicture} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.helper';
import {RecurringType} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {ALL_WEEKDAYS} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import {getGMTLabel, getGMTOffsetFromTimeZone} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {getTranslatedWeekdays} from '@Components/event-wizard/event-wizard-date-time-summary/event-wizard-date-time-summary.types';
import {CUSTOM_EVENT_ORGANISER_WIZARD_SECTION_OPENED} from '@Components/event-wizard/event-wizard-review/event-wizard-review.types';
import {CUSTOM_EVENT_WIZARD_WATERMARK_TOGGLED} from '@Components/event-wizard/event-wizard-other-options/event-wizard-other-options.types';
import {updateGoogleMapEmbedSrcLocation} from '@Components/google-map/google-map.helper';

const WIZARD_DEFAULT_CLASS = '-wizard-default';

const getIframeDocument = (): Document | undefined => {
  const iframe = document.getElementById(PREVIEW_IFRAME_ID) as HTMLIFrameElement;

  if (!iframe) {
    return undefined;
  }
  return iframe.contentDocument ?? iframe.contentWindow?.document;
};

const getIframeDocumentWindow = (): WindowProxy | undefined => {
  const iframe = document.getElementById(PREVIEW_IFRAME_ID) as HTMLIFrameElement;

  if (!iframe) {
    return undefined;
  }
  return iframe.contentWindow ?? undefined;
};

export const updatePreviewTitle = (title: string): void => {
  const titleElements = getElementsByClass(TITLE_ID);

  if (!titleElements) {
    return;
  }
  let newTitle = title;

  for (let i = 0; i < titleElements.length; i++) {
    const titleElement = titleElements[i];

    if (title === '') {
      newTitle = titleElement.getAttribute('data-placeholder') ?? window.i18next.t('pmwjs_new_event');
      addWizardDefaultClass(titleElement);
    } else {
      removeWizardDefaultClass(titleElement);
    }
    titleElement.textContent = newTitle;
  }
};

export const updatePreviewDescription = (description: string): void => {
  const descriptionElements = getElementsByClass(DESCRIPTION_ID);

  if (!descriptionElements) {
    return;
  }

  for (let i = 0; i < descriptionElements.length; i++) {
    const descriptionElement = descriptionElements[i];

    if (description === '') {
      addWizardDefaultClass(descriptionElement);
    } else {
      removeWizardDefaultClass(descriptionElement);
    }
    descriptionElement.innerHTML = description;
  }
};

export const updatePreviewVenue = (venue: EventWizardVenue): void => {
  updatePreviewSummaryVenueSection(venue);
  updatePreviewVenueSection(venue);
};

export const updatePreviewVenueAddressDetails = (address: string): void => {
  const addressEls = getElementsByClass(VENUE_MAP_ADDRESS_ID);

  if (!addressEls) {
    return;
  }

  for (let i = 0; i < addressEls.length; i++) {
    const addressEl = addressEls[i];
    addressEl.textContent = address;
  }
};

export const updatePreviewVenueAddressDirectionsDetails = (address: string): void => {
  const addressEls = getElementsByClass(VENUE_MAP_ADDRESS_DIRECTIONS_ID);

  if (!addressEls) {
    return;
  }

  for (let i = 0; i < addressEls.length; i++) {
    const addressEl = addressEls[i];
    addressEl.textContent = address;
  }
};

export const updatePreviewDateTime = (eventPreviewDateTime: EventPreviewDateTime): void => {
  const state = window.PMW.redux.store.getState().eventWizard;
  eventPreviewDateTime.recurringPattern = state.recurringEvent;

  if (!state.shouldShowRecurringEventDetails) {
    eventPreviewDateTime.recurringPattern = {...state.recurringEvent, isRecurring: false};
  }

  const getAllStateElements = (): Record<string, HTMLCollectionOf<Element> | null> => {
    return {
      [DATE_TIME_EMPTY_ID]: getElementsByClass(DATE_TIME_EMPTY_ID),
      [DATE_TIME_SINGLE_DAY_ID]: getElementsByClass(DATE_TIME_SINGLE_DAY_ID),
      [DATE_TIME_MULTIDAY_ID]: getElementsByClass(DATE_TIME_MULTIDAY_ID),
      [DATE_TIME_RECURRING_DAILY_ID]: getElementsByClass(DATE_TIME_RECURRING_DAILY_ID),
      [DATE_TIME_RECURRING_WEEKLY_SINGLE_ID]: getElementsByClass(DATE_TIME_RECURRING_WEEKLY_SINGLE_ID),
      [DATE_TIME_RECURRING_WEEKLY_MULTI_ID]: getElementsByClass(DATE_TIME_RECURRING_WEEKLY_MULTI_ID),
      [DATE_TIME_RECURRING_MONTHLY_ID]: getElementsByClass(DATE_TIME_RECURRING_MONTHLY_ID),
    };
  };

  const updateElementByState = (elements: HTMLCollectionOf<Element> | null): void => {
    if (!elements) {
      return;
    }
    const {startDateTime} = eventPreviewDateTime;
    const {endDateTime} = eventPreviewDateTime;

    displayElements(elements);

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      const monthIcon = getElementByClassFromElement(element, DATE_TIME_ICON_START_MONTH_ID);
      const dateIcon = getElementByClassFromElement(element, DATE_TIME_ICON_START_DATE_ID);
      const startTitle = getElementByClassFromElement(element, DATE_TIME_START_TITLE_ID);
      const startTitleMobile = getElementByClassFromElement(element, DATE_TIME_MOBILE_START_TITLE_ID);
      const startTitleMobile2 = startTitleMobile ? getElementByClassFromElement(startTitleMobile, '-bold') : null;
      const startTitleMobile3 = startTitleMobile ? getElementByClassFromElement(startTitleMobile, '-subtext') : null;
      const startSubtitle = getElementByClassFromElement(element, DATE_TIME_START_SUBTITLE_ID);
      const endTitle = getElementByClassFromElement(element, DATE_TIME_END_TITLE_ID);
      const endTitleMobile = getElementByClassFromElement(element, DATE_TIME_MOBILE_END_TITLE_ID);
      const endTitleMobile2 = endTitleMobile ? getElementByClassFromElement(endTitleMobile, '-bold') : null;
      const endTitleMobile3 = endTitleMobile ? getElementByClassFromElement(endTitleMobile, '-subtext') : null;
      const endSubtitle = getElementByClassFromElement(element, DATE_TIME_END_SUBTITLE_ID);

      let startTitleStr = null;
      let startTitleMobileStr = null;
      let startTitleMobileStr2 = null;
      let startTitleMobileStr3 = null;
      let startSubtitleStr = null;
      let endTitleStr = null;
      let endTitleMobileStr = null;
      let endTitleMobileStr2 = null;
      let endTitleMobileStr3 = null;
      let endSubtitleStr = null;

      if (monthIcon) {
        monthIcon.textContent = getReadableShortMonthForIndex(startDateTime.getMonth());
      }

      if (dateIcon) {
        dateIcon.textContent = startDateTime.getDate().toString();
      }

      if (isEmptyDateTime) {
        return;
      }
      const timezone = eventPreviewDateTime.timeZone;

      if (!isRecurring && isSameDate) {
        startTitleStr = startTitleMobileStr = getReadableDayMonthDateFormatForDate(startDateTime, true);
        endTitleStr = `${getReadableTimeForDate(startDateTime)} - ${getReadableTimeForDate(endDateTime)}`;
        endTitleMobileStr = `${getReadableTimeForDate(startDateTime)} - ${getReadableTimeForDate(endDateTime)} (${getGMTLabel(timezone)})`;
        endSubtitleStr = getGMTLabel(timezone);
      } else if (!isRecurring && !isSameDate) {
        startTitleStr = getReadableDayMonthDateFormatForDate(startDateTime, true);
        startTitleMobileStr2 = getMonthDateFormat(startDateTime);
        startTitleMobileStr3 = getReadableTimeForDate(startDateTime);
        startSubtitleStr = `${getReadableTimeForDate(startDateTime)} (${getGMTLabel(timezone)})`;
        endTitleStr = getReadableDayMonthDateFormatForDate(endDateTime, true);
        endTitleMobileStr2 = getMonthDateFormat(endDateTime);
        endTitleMobileStr3 = `${getReadableTimeForDate(endDateTime)} (${getGMTLabel(timezone)})`;
        endSubtitleStr = `${getReadableTimeForDate(endDateTime)} (${getGMTLabel(timezone)})`;
      } else if (isDailyRecurring) {
        startTitleStr = window.i18next.t('pmwjs_daily');
        endTitleStr = `${getReadableTimeForDate(startDateTime)} - ${getReadableTimeForDate(endDateTime)}`;
        endTitleMobileStr = `${endTitleStr} (${getGMTLabel(timezone)})`;
        endSubtitleStr = getGMTLabel(timezone);
      } else if (isWeeklySingleDayRecurring) {
        startTitleStr = startTitleMobileStr = getTranslatedWeekdays()[ALL_WEEKDAYS.indexOf(recurringPattern.recurringWeeks[0])];
        endTitleStr = `${getReadableTimeForDate(startDateTime)} - ${getReadableTimeForDate(endDateTime)}`;
        endTitleMobileStr = `${endTitleStr} (${getGMTLabel(timezone)})`;
        endSubtitleStr = getGMTLabel(timezone);
      } else if (isWeeklyMultiDayRecurring) {
        startTitleStr = window.i18next.t('pmwjs_weekly');
        startSubtitleStr = getExtraShortWeekdaysString(recurringPattern);
        startTitleMobileStr = `${startTitleStr} · ${startSubtitleStr}`;
        endTitleStr = `${getReadableTimeForDate(startDateTime)} - ${getReadableTimeForDate(endDateTime)}`;
        endTitleMobileStr = `${endTitleStr} (${getGMTLabel(timezone)})`;
        endSubtitleStr = getGMTLabel(timezone);
      } else if (isMonthlyRecurring) {
        startTitleStr = window.i18next.t('pmwjs_monthly');
        startSubtitleStr = recurringPattern.recurringMonthly.weekdayNum + ' ' + getTranslatedWeekdays()[ALL_WEEKDAYS.indexOf(recurringPattern.recurringMonthly.weekday)];
        startTitleMobileStr = `${startTitleStr} · ${startSubtitleStr}`;
        endTitleStr = `${getReadableTimeForDate(startDateTime)} - ${getReadableTimeForDate(endDateTime)}`;
        endTitleMobileStr = `${endTitleStr} (${getGMTLabel(timezone)})`;
        endSubtitleStr = getGMTLabel(timezone);
      }

      if (startTitle && startTitleStr) {
        startTitle.textContent = startTitleStr;
      }

      if (startTitleMobile && startTitleMobileStr) {
        startTitleMobile.textContent = startTitleMobileStr;
      }

      if (startSubtitle && startSubtitleStr) {
        startSubtitle.textContent = startSubtitleStr;
      }

      if (endTitle && endTitleStr) {
        endTitle.textContent = endTitleStr;
      }

      if (endTitleMobile && endTitleMobileStr) {
        endTitleMobile.textContent = endTitleMobileStr;
      }

      if (endSubtitle && endSubtitleStr) {
        endSubtitle.textContent = endSubtitleStr;
      }

      if (startTitleMobile2 && startTitleMobileStr2) {
        startTitleMobile2.textContent = startTitleMobileStr2;
      }

      if (startTitleMobile3 && startTitleMobileStr3) {
        startTitleMobile3.textContent = startTitleMobileStr3;
      }

      if (endTitleMobile2 && endTitleMobileStr2) {
        endTitleMobile2.textContent = endTitleMobileStr2;
      }

      if (endTitleMobile3 && endTitleMobileStr3) {
        endTitleMobile3.textContent = endTitleMobileStr3;
      }
    }
  };

  const {startDateTime, endDateTime} = eventPreviewDateTime;
  const dateTimeStateElements = getAllStateElements();
  let elementStateToUpdate: HTMLCollectionOf<Element> | null = null;

  hideElementsInDict(dateTimeStateElements);

  const isEmptyDateTime = !startDateTime || !endDateTime;
  const isSameDate = areDatesEqual(startDateTime, endDateTime);
  const isRecurring = eventPreviewDateTime.recurringPattern?.isRecurring;
  const recurringPattern = eventPreviewDateTime.recurringPattern;
  const isDailyRecurring = recurringPattern?.recurringType === RecurringType.DAILY;
  const isWeeklySingleDayRecurring = recurringPattern?.recurringType === RecurringType.WEEKLY && recurringPattern.recurringWeeks?.length === 1;
  const isWeeklyMultiDayRecurring = recurringPattern?.recurringType === RecurringType.WEEKLY && recurringPattern.recurringWeeks?.length > 1;
  const isMonthlyRecurring = recurringPattern?.recurringType === RecurringType.MONTHLY && recurringPattern.recurringMonthly;

  if (isEmptyDateTime) {
    elementStateToUpdate = dateTimeStateElements[DATE_TIME_EMPTY_ID];
  } else if (!isRecurring && isSameDate) {
    elementStateToUpdate = dateTimeStateElements[DATE_TIME_SINGLE_DAY_ID];
  } else if (!isRecurring && !isSameDate) {
    elementStateToUpdate = dateTimeStateElements[DATE_TIME_MULTIDAY_ID];
  } else if (isDailyRecurring) {
    elementStateToUpdate = dateTimeStateElements[DATE_TIME_RECURRING_DAILY_ID];
  } else if (isWeeklySingleDayRecurring) {
    elementStateToUpdate = dateTimeStateElements[DATE_TIME_RECURRING_WEEKLY_SINGLE_ID];
  } else if (isWeeklyMultiDayRecurring) {
    elementStateToUpdate = dateTimeStateElements[DATE_TIME_RECURRING_WEEKLY_MULTI_ID];
  } else if (isMonthlyRecurring) {
    elementStateToUpdate = dateTimeStateElements[DATE_TIME_RECURRING_MONTHLY_ID];
  }
  updateElementByState(elementStateToUpdate);
};

export const updatePreviewTimeZone = (timeZone: string): void => {
  const timeZoneEls = getElementsByClass(DATE_TIME_TIMEZONE_ID);

  if (!timeZoneEls || timeZoneEls.length === 0) {
    return;
  }

  for (let i = 0; i < timeZoneEls.length; i++) {
    const timeZoneEl = timeZoneEls[i];
    timeZoneEl.textContent = timeZone;
  }
};

export const updatePreviewCountdown = (startDateTime: Date | undefined, timeZone: TimeZone): void => {
  const countdownElements = getElementsByClass(COUNTDOWN_ID);

  if (!countdownElements) {
    return;
  }

  if (!startDateTime) {
    hideElementsInCollection(countdownElements);
    return;
  }
  displayElements(countdownElements);

  const diffInMillis = getTimeDifferenceFromNowInMilliseconds(startDateTime, getTimeZoneOffsetFromUTCOffsetInMinutes(getGMTOffsetFromTimeZone(timeZone)));

  const currentTime = new Date(),
    startTimestamp = Math.floor((currentTime.getTime() + diffInMillis) / 1000);
  getIframeDocumentWindow()?.dispatchEvent(
    new CustomEvent(CUSTOM_EVENT_COUNTDOWN_WIZARD_UPDATED, {
      detail: {
        startTimestamp: startTimestamp,
      },
    })
  );
};

export const toggleDisplayPreviewCoverPhotoSection = (shouldDisplay = true): void => {
  const els = getElementsByClass(COVER_PHOTO_CONTAINER_ID);

  if (shouldDisplay) {
    displayElements(els);
  } else {
    hideElementsInCollection(els);
  }
};

/**
 * Makes each shortcut's elements in the preview clickable; a click announces the shortcut's target with
 * PREVIEW_SHORTCUT_CLICKED_EVENT, and the step 1 field it names reacts with its own state. The preview page ignores the
 * pointer (_pointer-events-none on html); PREVIEW_SHORTCUT_CLASS turns it back on for these elements alone. Returns
 * the function that undoes it.
 */
export const enablePreviewShortcuts = (shortcuts: PreviewShortcut[]): VoidFunction => {
  const undoShortcuts = shortcuts.map(enablePreviewShortcut);

  return (): void => {
    for (const undoShortcut of undoShortcuts) {
      undoShortcut();
    }
  };
};

const enablePreviewShortcut = (shortcut: PreviewShortcut): VoidFunction => {
  const shortcutEls = shortcut.elementClasses.flatMap((elementClass) => {
    return Array.from(getElementsByClass(elementClass) ?? []);
  });
  const announceShortcutClicked = (): void => {
    replayClickOnPreviewIframe();
    window.dispatchEvent(new CustomEvent<PreviewShortcutClickedDetail>(PREVIEW_SHORTCUT_CLICKED_EVENT, {detail: {target: shortcut.target}}));
  };

  for (const shortcutEl of shortcutEls) {
    shortcutEl.classList.add(PREVIEW_SHORTCUT_CLASS);
    shortcutEl.addEventListener('click', announceShortcutClicked);
  }

  return (): void => {
    for (const shortcutEl of shortcutEls) {
      shortcutEl.classList.remove(PREVIEW_SHORTCUT_CLASS);
      shortcutEl.removeEventListener('click', announceShortcutClicked);
    }
  };
};

/**
 * A click inside the preview never reaches the wizard's document, so the wizard's outside-click handlers (an open
 * menu, the expanded date and time panel) miss it. Replays it on the iframe element, where the user did click, so
 * they close as they would for any click outside them before the shortcut opens its own field.
 */
const replayClickOnPreviewIframe = (): void => {
  const iframe = document.getElementById(PREVIEW_IFRAME_ID);

  for (const eventType of OUTSIDE_CLICK_EVENT_TYPES) {
    iframe?.dispatchEvent(new MouseEvent(eventType, {bubbles: true}));
  }
};

export const togglePreviewCoverPhotoLoading = (isLoading: boolean): void => {
  const coverPhotoContainerEls: HTMLCollectionOf<Element> | null = getElementsByClass(COVER_PHOTO_CONTAINER_ID);

  if (coverPhotoContainerEls && coverPhotoContainerEls.length > 0) {
    for (let i = 0; i < coverPhotoContainerEls.length; i++) {
      const coverPhotoContainerEl = coverPhotoContainerEls[i];

      if (isLoading) {
        coverPhotoContainerEl.classList.add('-loading');
      } else {
        coverPhotoContainerEl.classList.remove('-loading');
      }
    }
  }
};

export const updatePreviewCoverPhoto = async (url: string | undefined): Promise<void> => {
  async function isLandscape(imageUrl: string): Promise<boolean> {
    try {
      const response = await fetch(imageUrl);

      if (!response.ok) {
        return false;
      }
      const blob = await response.blob();
      const img = new Image();

      return await new Promise((resolve, reject) => {
        img.src = URL.createObjectURL(blob);
        img.onload = (): void => {
          resolve(img.width > img.height);
        };
        img.onerror = (): void => {
          reject(new Error('Failed to load image'));
        };
      });
    } catch (e) {
      return false;
    }
  }

  const coverPhotoContainerEls = getElementsByClass(COVER_PHOTO_CONTAINER_ID);
  const coverPhotoEls = getElementsByClass(COVER_PHOTO_ID);
  const coverPhotoBgEls = getElementsByClass(COVER_PHOTO_BACKGROUND_ID);
  const coverPhotoPlaceholderEls = getElementsByClass(COVER_PHOTO_PLACEHOLDER_ICON_ID);

  toggleDisplayPreviewCoverPhotoSection(true);

  if (coverPhotoContainerEls && coverPhotoContainerEls.length > 0) {
    for (let i = 0; i < coverPhotoContainerEls.length; i++) {
      const coverPhotoContainerEl = coverPhotoContainerEls[i];

      if (url && url !== '') {
        displayElement(coverPhotoContainerEl);
        void isLandscape(url).then((isLandscape) => {
          if (isLandscape) {
            coverPhotoContainerEl.classList.add('-landscape');
          } else {
            coverPhotoContainerEl.classList.remove('-landscape');
          }
        });
      }
    }
  }

  if (coverPhotoEls && coverPhotoEls.length > 0) {
    for (let i = 0; i < coverPhotoEls.length; i++) {
      const coverPhotoEl = coverPhotoEls[i];

      if (!url || url === '') {
        hideElement(coverPhotoEl);
      } else {
        coverPhotoEl.setAttribute('src', url);
        displayElement(coverPhotoEl);
      }
    }
  }

  if (coverPhotoBgEls && coverPhotoBgEls.length > 0) {
    for (let i = 0; i < coverPhotoBgEls.length; i++) {
      const coverPhotoBgEl = coverPhotoBgEls[i] as HTMLElement;

      if (!url || url === '') {
        coverPhotoBgEl.style.background = `linear-gradient(180deg, rgba(255, 255, 255, 0.60) 12.03%, #FFF 89.17%), url('') lightgray 50% / cover no-repeat`;
      } else {
        coverPhotoBgEl.style.background = `linear-gradient(180deg, rgba(255, 255, 255, 0.60) 12.03%, #FFF 89.17%), url('${url}') lightgray 50% / cover no-repeat`;
        displayElement(coverPhotoBgEl);
      }
    }
  }

  if (coverPhotoPlaceholderEls && coverPhotoPlaceholderEls.length > 0) {
    for (let i = 0; i < coverPhotoPlaceholderEls.length; i++) {
      const coverPhotoPlaceholderEl = coverPhotoPlaceholderEls[i];

      if (!url || url === '') {
        displayElement(coverPhotoPlaceholderEl);
      } else {
        hideElement(coverPhotoPlaceholderEl);
      }
    }
  }
  togglePreviewCoverPhotoLoading(false);
};

export const updatePreviewOrganiserVisibilityToShow = (): void => {
  getIframeDocumentWindow()?.dispatchEvent(new CustomEvent(CUSTOM_EVENT_ORGANISER_WIZARD_SECTION_OPENED));
};

export const updatePreviewOrganiserDetails = (organiserDetails: OrganiserDetails): void => {
  const organiserProfilePictureEls = getElementsByClass(ORGANISER_PROFILE_PICTURE);
  const organiserNameEls = getElementsByClass(ORGANISER_NAME);
  const organiserEmailEls = getElementsByClass(ORGANISER_EMAIL);
  const organiserPhoneEls = getElementsByClass(ORGANISER_PHONE);

  if (organiserProfilePictureEls) {
    for (let i = 0; i < organiserProfilePictureEls.length; i++) {
      const organiserProfilePictureEl = organiserProfilePictureEls[i];
      organiserProfilePictureEl.setAttribute('src', getOrganiserProfilePicture(organiserDetails));
    }
  }

  if (organiserNameEls) {
    for (let i = 0; i < organiserNameEls.length; i++) {
      const organiserNameEl = organiserNameEls[i];
      organiserNameEl.textContent = organiserDetails.name ? organiserDetails.name : window.i18next.t('pmwjs_name');
    }
  }

  if (organiserEmailEls) {
    for (let i = 0; i < organiserEmailEls.length; i++) {
      const organiserEmailEl = organiserEmailEls[i];
      organiserEmailEl.textContent = organiserDetails.email ? organiserDetails.email : window.i18next.t('pmwjs_email');
    }
  }

  if (organiserPhoneEls) {
    for (let i = 0; i < organiserPhoneEls.length; i++) {
      const organiserPhoneEl = organiserPhoneEls[i];
      organiserPhoneEl.textContent = organiserDetails.phone ? organiserDetails.phone : window.i18next.t('pmwjs_phone');
    }
  }
  updateOrganiserSocialDetails(organiserDetails);
};

const updateOrganiserSocialDetails = (organiserDetails: OrganiserDetails): void => {
  const {socialMediaLinks} = organiserDetails;
  const organiserSocialEls = getElementsByClass(ORGANISER_SOCIAL_CONTAINER);
  const organiserFacebookEls = getElementsByClass(ORGANISER_SOCIAL_FACEBOOK);
  const organiserInstagramEls = getElementsByClass(ORGANISER_SOCIAL_INSTAGRAM);
  const organiserLinkedinEls = getElementsByClass(ORGANISER_SOCIAL_LINKEDIN);
  const organiserXEls = getElementsByClass(ORGANISER_SOCIAL_X);
  const organiserPinterestEls = getElementsByClass(ORGANISER_SOCIAL_PINTEREST);
  const organiserYoutubeEls = getElementsByClass(ORGANISER_SOCIAL_YOUTUBE);
  const organiserTiktokEls = getElementsByClass(ORGANISER_SOCIAL_TIKTOK);

  const updateSocialMediaEls = (link: string, els: HTMLCollectionOf<Element> | null): void => {
    if (!els) {
      return;
    }

    if (link) {
      displayElements(els);
    } else {
      hideElementsInCollection(els);
    }
  };
  updateSocialMediaEls(socialMediaLinks.facebook, organiserFacebookEls);
  updateSocialMediaEls(socialMediaLinks.instagram, organiserInstagramEls);
  updateSocialMediaEls(socialMediaLinks.linkedin, organiserLinkedinEls);
  updateSocialMediaEls(socialMediaLinks.x, organiserXEls);
  updateSocialMediaEls(socialMediaLinks.pinterest, organiserPinterestEls);
  updateSocialMediaEls(socialMediaLinks['youtube-channel'], organiserYoutubeEls);
  updateSocialMediaEls(socialMediaLinks['tiktok-profile'], organiserTiktokEls);

  if (
    socialMediaLinks.facebook ||
    socialMediaLinks.instagram ||
    socialMediaLinks.linkedin ||
    socialMediaLinks.x ||
    socialMediaLinks.pinterest ||
    socialMediaLinks['youtube-channel'] ||
    socialMediaLinks['tiktok-profile']
  ) {
    displayElements(organiserSocialEls);
  } else {
    hideElementsInCollection(organiserSocialEls);
  }
};

const toggleDisplayPreviewRegisterCTAs = (shouldDisplay: boolean): void => {
  const registrationCTAs = getElementsByClass('register-btn');

  if (!registrationCTAs || registrationCTAs.length === 0) {
    return;
  }

  for (let i = 0; i < registrationCTAs.length; i++) {
    const registrationCTA = registrationCTAs[i];
    if (shouldDisplay) {
      displayElement(registrationCTA);
    } else {
      hideElement(registrationCTA);
    }
  }
};

export const updatePreviewGuestInfoPhoneVisibility = (isToggled: boolean): void => {
  if (isToggled) {
    getIframeDocumentWindow()?.dispatchEvent(new CustomEvent(CUSTOM_EVENT_GUEST_INFO_PHONE_TOGGLED));
  }
};

const updatePreviewRegistrationCTAText = (ctaText: string): void => {
  const registrationCTAs = getElementsByClass(REGISTRATION_CTA_ID);

  if (!registrationCTAs || registrationCTAs.length === 0) {
    return;
  }

  for (let i = 0; i < registrationCTAs.length; i++) {
    const registrationCTA = registrationCTAs[i];
    registrationCTA.textContent = ctaText;
  }
};

/**
 * Reflects the register/Buy CTA state in the preview: visibility and button text. TICKETS and RSVP are
 * ticketing modes with areRegistrationsRequired=false (tickets/RSVP ARE the registration), but both still
 * need the CTA shown as the Buy/RSVP button — on the real page event-page-ticketing.js swaps it in via
 * AJAX, which never runs in the wizard iframe, so the preview must show the existing register CTA for them
 * too. The label comes from the mode's effective CTA text (per-mode ticketing button text for TICKETS/RSVP,
 * the custom-link/default field otherwise), matching what the published page renders. NONE mode hides it.
 */
export const updatePreviewRegisterButton = (fields: EventRegistrationFields, mode: RegistrationMode, ticketingConfig: TicketingConfig): void => {
  toggleDisplayPreviewRegisterCTAs(fields.areRegistrationsRequired || mode === RegistrationMode.TICKETS || mode === RegistrationMode.RSVP);
  updatePreviewRegistrationCTAText(getEffectiveRegisterBtnText(mode, ticketingConfig, fields));
};

/**
 * What the buyer is charged, for the preview's order-summary total: the ticket price plus tax, plus the fees on top
 * when the organiser passes them on. `attendeePays` already answers for both fee modes, and is the same field the
 * View-details panel totals against, so the preview cannot disagree with it. Null until the fee rates load, which
 * leaves the preview showing the ticket price — only wrong in pass mode, and only for that moment.
 */
const getPreviewCheckoutTotal = (config: TicketingConfig, ticketingFees: TicketingFeeParams | null): number | null => {
  if (config.price <= 0 || ticketingFees === null) {
    return null;
  }
  return computeTicketPriceBreakdown(config, ticketingFees).attendeePays;
};

/**
 * Signals the wizard iframe to open/update/close the checkout-preview dialog with the
 * current draft config. The dialog is built inside the iframe by
 * event-page-ticketing-wizard-preview.js (via openModalDialog) — React only forwards config.
 * Event title/date/cover feed the RSVP step-1 event card.
 */
export const syncPreviewTicketingQuestions = (args: WizardCheckoutPreviewSyncArgs): void => {
  const {config, mode, step} = args;
  const show = (mode === RegistrationMode.RSVP || mode === RegistrationMode.TICKETS) && step === EventWizardProgressStep.TICKETING;
  if (!show) {
    getIframeDocumentWindow()?.dispatchEvent(new CustomEvent(CUSTOM_EVENT_WIZARD_CHECKOUT_SYNC, {detail: {show: false}}));
    return;
  }
  getIframeDocumentWindow()?.dispatchEvent(
    new CustomEvent(CUSTOM_EVENT_WIZARD_CHECKOUT_SYNC, {
      detail: {
        show,
        isRsvp: mode === RegistrationMode.RSVP,
        price: config.price,
        checkoutTotal: getPreviewCheckoutTotal(config, args.ticketingFees),
        whoResponds: config.whoResponds,
        sectionTitle: getEffectiveQuestionSectionTitle(config),
        sectionDescription: config.questionSectionDescription,
        eventTitle: args.eventTitle,
        eventDate: getReadableDayMonthDateFormatForDate(args.startDateTime, true),
        eventTime: getReadableTimeForDate(args.startDateTime),
        thumbnailUrl: args.coverPhotoUrl ?? '',
        questions: config.questions,
        donation: config.donation,
      },
    })
  );
};

export const updatePreviewWatermark = (isRemoveWatermark: boolean, shouldScrollToElement = false): void => {
  const watermarkFieldEls = getElementsByClass(WATERMARK_ID);
  const pageContainerEl = getElementByID(EVENT_PAGE_MAIN_CONTAINER_ID);

  if (shouldScrollToElement) {
    getIframeDocumentWindow()?.dispatchEvent(new CustomEvent(CUSTOM_EVENT_WIZARD_WATERMARK_TOGGLED, {detail: {isRemoveWatermark: isRemoveWatermark}}));
  } else {
    if (isRemoveWatermark) {
      hideElementsInCollection(watermarkFieldEls);
      pageContainerEl?.classList?.add('-no-watermark');
    } else {
      displayElements(watermarkFieldEls);
      pageContainerEl?.classList?.remove('-no-watermark');
    }
  }
};

/**
 * Strip the inline hover handlers the server writes for Mode A.
 *
 * They are the reason a Mode A page's Register button hovers at all — its inline background beats
 * every stylesheet, so the colour has to be swapped by JS. On the themed path they are actively
 * harmful and *were* the cause of a real bug: the preview iframe is rendered once and then mutated,
 * so an event that was Mode A at render time kept `onmouseover`/`onmouseout` pointing at its old
 * palette. Clearing only the inline style left those attributes live, and the first hover wrote the
 * stale colour straight back inline — where, being inline, it stuck.
 *
 * Removing them does mean a Mode A → B → A round trip leaves the preview's hover inert until the
 * iframe reloads. That is a fidelity gap in a preview, against a wrong colour that persisted.
 */
const removeInlineHoverHandlers = (element: HTMLElement): void => {
  INLINE_HOVER_ATTRIBUTES.forEach((attribute) => {
    element.removeAttribute(attribute);
  });
};

/**
 * Paint (or deliberately un-paint) the Register button inside the page container.
 *
 * Mode A writes the fill and the label inline, exactly as `EventStyleVO::getButtonAttributes` does
 * on the server — and both, because updating only the background left a Mode A page previewed in
 * Mode B with its old white label on the new accent at 2.2:1.
 *
 * Mode B writes nothing and clears whatever a previous Mode A render left behind. The themed
 * stylesheet owns this button's fill, label and `:hover`/`:focus`/`:active` states, and an inline
 * background silently beats all of them; the server stopped emitting one here for the same reason,
 * so clearing it is also what keeps the preview and the public page identical (probe P4).
 */
const paintStyleColorElement = (element: HTMLElement, colors: EventColorScheme): void => {
  if (colors.background) {
    element.style.removeProperty('background');
    element.style.removeProperty('background-color');
    element.style.removeProperty('color');
    element.style.removeProperty(EVENT_PAGE_ACTION_LABEL_CSS_VAR);
    removeInlineHoverHandlers(element);
    return;
  }
  element.style.background = colors.primary;
  if (colors.buttonTextColor) {
    // The custom property is the one that lands on the label; the plain `color` is kept only so the
    // parts of the button the `brand-action-label()` mixin does not name still follow. Mirrors
    // EventStyleVO::getActionLabelStyleForGivenColor — the two must not drift, or the preview and
    // the saved page disagree about a colour the user is choosing by eye.
    element.style.setProperty(EVENT_PAGE_ACTION_LABEL_CSS_VAR, colors.buttonTextColor);
    element.style.color = colors.buttonTextColor;
  }
};

/**
 * Give the preview's ticket-checkout dialog its action colours, on the iframe's **document root**.
 *
 * The root, not the page container, and not the dialog: the wizard clones the checkout template into a
 * body-appended modal, so it leaves the page container and inherits from `<html>` instead. Setting them
 * high also means the values are already in place whenever the dialog is opened, and stay correct when it
 * is open and the colour changes — no copying, and no dependence on the dialog's own (legacy, aggressively
 * cached) script.
 *
 * Mode-agnostic by construction: `primary` is the brand colour in Mode A and the derived accent in Mode B,
 * which is why the button follows the organiser in both. Mirrors
 * `EventStyleVO::getActionColorVariablesStyle` on the server.
 */
const applyCheckoutActionColors = (colors: EventColorScheme): void => {
  const root = getIframeDocument()?.documentElement;

  if (!root) {
    return;
  }
  root.style.setProperty(EVENT_PAGE_ACTION_FILL_CSS_VAR, colors.primary);
  root.style.setProperty(EVENT_PAGE_ACTION_FILL_HOVER_CSS_VAR, colors.darker);

  if (colors.buttonTextColor) {
    root.style.setProperty(EVENT_PAGE_ACTION_LABEL_CSS_VAR, colors.buttonTextColor);
  }
};

export const updateStyleColor = (colors: EventColorScheme): void => {
  const lighterBgEls = getElementsByClass(STYLE_LIGHTER_BG_COLOR_ID);
  const lightBgEls = getElementsByClass(STYLE_LIGHT_BG_COLOR_ID);
  const darkTextEls = getElementsByClass(STYLE_DARK_TEXT_COLOR_ID);
  const selectedStyleEls = getElementsByClass(STYLE_COLOR_ID);
  const pageContainer = getElementByID(EVENT_PAGE_MAIN_CONTAINER_ID);

  const pageTheme = getEventPageThemeFromPalette(colors, PMW_COLORS_NEUTRAL.NEUTRAL_0);
  applyCheckoutActionColors(colors);

  if (pageContainer) {
    applyEventPageTheme(pageContainer as HTMLElement, pageTheme, EVENT_PAGE_CUSTOM_BG_CLASS);
  }

  applyEventPageThemeToFooter(getIframeDocument(), pageTheme);

  if (lighterBgEls) {
    for (let i = 0; i < lighterBgEls.length; i++) {
      const lighterBgEl = lighterBgEls[i] as HTMLElement;
      lighterBgEl.style.background = colors.lighter;
    }
  }

  if (lightBgEls) {
    for (let i = 0; i < lightBgEls.length; i++) {
      const lightBgEl = lightBgEls[i] as HTMLElement;
      lightBgEl.style.background = colors.light;
    }
  }

  if (darkTextEls) {
    for (let i = 0; i < darkTextEls.length; i++) {
      const darkTextEl = darkTextEls[i] as HTMLElement;
      darkTextEl.style.color = colors.dark;
    }
  }

  if (selectedStyleEls) {
    for (let i = 0; i < selectedStyleEls.length; i++) {
      paintStyleColorElement(selectedStyleEls[i] as HTMLElement, colors);
    }
  }
};

export const updateStyleFont = (font: string): void => {
  const iframeDoc = getIframeDocument();

  if (iframeDoc) {
    const titleEls = getElementsByClass(TITLE_ID);

    if (!titleEls) {
      return;
    }

    for (let i = 0; i < titleEls?.length; i++) {
      const titleEl = titleEls[i];
      titleEl.classList.add('-loading');
    }
    loadFontFamilyInIframe(iframeDoc, font);
  }
};

export const togglePreviewTitleLoading = (isLoading: boolean): void => {
  const titleEls = getElementsByClass(TITLE_ID);

  if (!titleEls) {
    return;
  }

  for (let i = 0; i < titleEls?.length; i++) {
    const titleEl = titleEls[i];

    if (isLoading) {
      titleEl.classList.add('-loading');
    } else {
      titleEl.classList.remove('-loading');
    }
  }
};

const updatePreviewSummaryVenueSection = (venue: EventWizardVenue): void => {
  const getAllStateElements = (): {[p: string]: HTMLCollectionOf<Element> | null} => {
    return {
      [VENUE_ICON_EMPTY_ID]: getElementsByClass(VENUE_ICON_EMPTY_ID),
      [VENUE_ICON_MAP_ID]: getElementsByClass(VENUE_ICON_MAP_ID),
      [VENUE_ICON_GOOGLE_MEET_ID]: getElementsByClass(VENUE_ICON_GOOGLE_MEET_ID),
      [VENUE_ICON_ZOOM_ID]: getElementsByClass(VENUE_ICON_ZOOM_ID),
      [VENUE_ICON_ONLINE_ID]: getElementsByClass(VENUE_ICON_ONLINE_ID),
      [VENUE_ICON_ONLINE_NON_URL_ID]: getElementsByClass(VENUE_ICON_ONLINE_NON_URL_ID),
    };
  };

  const updateOfflineDetails = (elements: HTMLCollectionOf<Element> | null, offlineVenueDetails: PlaceDetails): void => {
    if (!elements) {
      return;
    }

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      const titleElement = getElementByClassFromElement(element, VENUE_SUMMARY_TITLE);
      const subtitleElement = getElementByClassFromElement(element, VENUE_SUMMARY_SUBTITLE);

      if (titleElement) {
        titleElement.textContent = offlineVenueDetails.name ?? null;
      }

      if (subtitleElement) {
        subtitleElement.textContent = offlineVenueDetails.address ?? null;
      }
    }
  };

  const updateOnlineDetails = (elements: HTMLCollectionOf<Element> | null, onlineVenueURL: string): void => {
    if (!elements) {
      return;
    }
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      element.textContent = onlineVenueURL;
    }
  };

  const venueSummaryStateElements = getAllStateElements();

  hideElementsInDict(venueSummaryStateElements);

  if (venue.offlineVenue) {
    displayElements(venueSummaryStateElements[VENUE_ICON_MAP_ID]);
    updateOfflineDetails(venueSummaryStateElements[VENUE_ICON_MAP_ID], venue.offlineVenue);
  } else if (venue.onlineVenueURL) {
    let onlineStateID = VENUE_ICON_ONLINE_ID,
      textToReplace = '';

    if (!isLikelyUrl(venue.onlineVenueURL)) {
      onlineStateID = VENUE_ICON_ONLINE_NON_URL_ID;
      textToReplace = venue.onlineVenueURL;
    } else if (isGoogleMeetLink(venue.onlineVenueURL)) {
      onlineStateID = VENUE_ICON_GOOGLE_MEET_ID;
    } else if (isZoomLink(venue.onlineVenueURL)) {
      onlineStateID = VENUE_ICON_ZOOM_ID;
    }
    updateOnlineDetails(getElementsByClass(VENUE_SUMMARY_SUBTITLE), textToReplace);
    displayElements(venueSummaryStateElements[onlineStateID]);
  } else {
    displayElements(venueSummaryStateElements[VENUE_ICON_EMPTY_ID]);
  }
};

const updatePreviewVenueSection = (venue: EventWizardVenue): void => {
  const getAllStateElements = (): {[p: string]: HTMLCollectionOf<Element> | null} => {
    return {
      [VENUE_MAP_ID]: getElementsByClass(VENUE_MAP_ID),
      [VENUE_ONLINE_ID]: getElementsByClass(VENUE_ONLINE_ID),
      [VENUE_ONLINE_NON_URL_ID]: getElementsByClass(VENUE_ONLINE_NON_URL_ID),
      [VENUE_ONLINE_GOOGLE_MEET_ID]: getElementsByClass(VENUE_ONLINE_GOOGLE_MEET_ID),
      [VENUE_ONLINE_ZOOM_ID]: getElementsByClass(VENUE_ONLINE_ZOOM_ID),
    };
  };

  const updateOfflineDetails = (elements: HTMLCollectionOf<Element> | null, offlineVenueDetails: PlaceDetails): void => {
    if (!elements) {
      return;
    }

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      const titleElement = getElementByClassFromElement(element, VENUE_MAP_NAME_ID);
      const subtitleElement = getElementByClassFromElement(element, VENUE_MAP_ADDRESS_ID);
      const venueMapLocationElement = getElementByClassFromElement(element, VENUE_MAP_LOCATION_ID);

      if (titleElement) {
        titleElement.textContent = offlineVenueDetails.name ?? null;
      }

      if (subtitleElement) {
        subtitleElement.textContent = offlineVenueDetails.address ?? null;
      }

      if (venueMapLocationElement?.hasAttribute('src')) {
        const src = updateGoogleMapEmbedSrcLocation(venueMapLocationElement?.getAttribute('src'), offlineVenueDetails);
        venueMapLocationElement?.classList.add('_opacity-0');
        venueMapLocationElement?.setAttribute('src', src);
      }
    }
  };

  const updateOnlineDetails = (elements: HTMLCollectionOf<Element> | null, onlineVenueURL: string): void => {
    if (!elements) {
      return;
    }
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      element.textContent = `- ${onlineVenueURL}`;
    }
  };

  const venueStateElements = getAllStateElements();

  hideElementsInDict(venueStateElements);

  if (venue.offlineVenue) {
    displayElements(venueStateElements[VENUE_MAP_ID]);
    updateOfflineDetails(venueStateElements[VENUE_MAP_ID], venue.offlineVenue);
  } else if (venue.onlineVenueURL) {
    let onlineStateID = VENUE_ONLINE_ID;

    if (!isLikelyUrl(venue.onlineVenueURL)) {
      onlineStateID = VENUE_ONLINE_NON_URL_ID;
      updateOnlineDetails(getElementsByClass(VENUE_ONLINE_NON_URL_TEXT_ID), venue.onlineVenueURL);
    } else if (isGoogleMeetLink(venue.onlineVenueURL)) {
      onlineStateID = VENUE_ONLINE_GOOGLE_MEET_ID;
    } else if (isZoomLink(venue.onlineVenueURL)) {
      onlineStateID = VENUE_ONLINE_ZOOM_ID;
    }
    displayElements(venueStateElements[onlineStateID]);
  }
};

const getElementsByClass = (className: string): HTMLCollectionOf<Element> | null => {
  const iframeDocument = getIframeDocument();

  if (!iframeDocument) {
    return null;
  }
  const elements = iframeDocument.getElementsByClassName(className);

  if (!elements || elements.length === 0) {
    return null;
  }
  return elements;
};

const getElementByID = (id: string): Element | null => {
  const iframeDocument = getIframeDocument();

  if (!iframeDocument) {
    return null;
  }
  const element = iframeDocument.getElementById(id);

  if (!element) {
    return null;
  }
  return element;
};

const getElementByClassFromElement = (element: Element, className: string): Element | null => {
  const elements = element.getElementsByClassName(className);

  if (elements && elements.length > 0) {
    return elements[0];
  }
  return null;
};

const hideElementsInDict = (elementsDict: {[p: string]: HTMLCollectionOf<Element> | null}): void => {
  Object.keys(elementsDict).forEach((key) => {
    const elements = elementsDict[key];
    hideElementsInCollection(elements);
  });
};

const hideElementsInCollection = (elements: HTMLCollectionOf<Element> | null): void => {
  if (elements) {
    Array.from(elements).forEach((elementToClassUpdate) => {
      hideElement(elementToClassUpdate);
    });
  }
};

const hideElement = (element: Element | null): void => {
  if (element) {
    element.classList.add('_hidden');
  }
};

const displayElements = (elements: HTMLCollectionOf<Element> | null): void => {
  if (!elements) {
    return;
  }

  for (let i = 0; i < elements.length; i++) {
    displayElement(elements[i]);
  }
};

const displayElement = (element: Element | null): void => {
  if (!element) {
    return;
  }
  element.classList.remove('_hidden');
};

const addWizardDefaultClass = (element: Element): void => {
  element.classList.add(WIZARD_DEFAULT_CLASS);
};

const removeWizardDefaultClass = (element: Element): void => {
  element.classList.remove(WIZARD_DEFAULT_CLASS);
};
